import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { ActivePlanState, LegacyPlan } from "../../assets/interfaces/interfaces";
import { doSignOutClear } from "../../components/auth/helpers";

export const deriveLegacyPlan = (data): LegacyPlan | null => {
  if (!data || typeof data !== "object") return null;
  if (data.legacyPlan !== undefined) return data.legacyPlan;
  const sub = data.subscription;
  const provider = data.subscription_provider ?? sub?.provider;
  if (
    !data.comped
    && provider === "trade_gpt"
    && (sub?.name === "Pro" || sub?.name === "Premium")
  ) {
    return {
      type: sub.name,
      price_monthly: Number(sub.price ?? 0) / 100,
      billing_period: sub.billing_period ?? "month",
      migrate_by: sub.expires_at,
    };
  }
  return null;
};

// legacyPlan stays set until the term ends, so an accepted migration only shows up as the scheduled set.
export const hasScheduledMigration = (plan?: ActivePlanState["plan"]) =>
  !!plan?.legacyPlan && !!plan?.scheduledChanges?.bundles?.length;

const initialState: ActivePlanState = {
  plan: {
    promptsLeft: 0,
    subscription_type: "Free",
    subscription: {},
  },
  status: "idle",
  error: null,
  migrationFlowOpen: false,
};
const getActivePlanPayload = async () => {
  const { data } = await axios.get(
    `${process.env.REACT_APP_SPL_URL}/v2/subscriptions/active`,
    { withCredentials: true },
  );
  return data && typeof data === "object"
    ? { ...data, legacyPlan: deriveLegacyPlan(data) }
    : data;
};

const applyPlanFields = (state: ActivePlanState, payload: unknown) => {
  if (!payload || typeof payload !== "object") return;
  const {
    promptsLeft, subscription_type, subscription,
    pro, modelOptions, ownedBundles, dailyPrompts, monthlySpend,
    billingPeriod, comped, subscription_provider, non_renewing,
    scheduledChanges, endingBundles, renewalCharge, legacyPlan,
  } = payload as Partial<ActivePlanState["plan"]>;
  if (promptsLeft !== undefined) state.plan.promptsLeft = promptsLeft;
  if (subscription_type !== undefined) state.plan.subscription_type = subscription_type;
  if (subscription !== undefined) state.plan.subscription = subscription;
  if (pro !== undefined) state.plan.pro = pro;
  if (modelOptions !== undefined) state.plan.modelOptions = modelOptions;
  if (ownedBundles !== undefined) state.plan.ownedBundles = ownedBundles;
  if (dailyPrompts !== undefined) state.plan.dailyPrompts = dailyPrompts;
  if (monthlySpend !== undefined) state.plan.monthlySpend = monthlySpend;
  if (billingPeriod !== undefined) state.plan.billingPeriod = billingPeriod;
  if (comped !== undefined) state.plan.comped = comped;
  if (subscription_provider !== undefined) state.plan.subscription_provider = subscription_provider;
  if (non_renewing !== undefined) state.plan.non_renewing = non_renewing;
  if (scheduledChanges !== undefined) state.plan.scheduledChanges = scheduledChanges;
  if (endingBundles !== undefined) state.plan.endingBundles = endingBundles;
  if (renewalCharge !== undefined) state.plan.renewalCharge = renewalCharge;
  state.plan.legacyPlan = legacyPlan ?? null;
};

type FetchActivePlanOptions = { silent?: boolean };
const isSilent = (opts: FetchActivePlanOptions | void) => (opts as FetchActivePlanOptions | undefined)?.silent === true;

export const fetchActivePlan = createAsyncThunk(
  "plan/fetchActivePlan",
  async (opts: FetchActivePlanOptions | void, { dispatch, rejectWithValue }) => {
    try {
      return await getActivePlanPayload();
    }
    catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response.status === 403) {
          if (!isSilent(opts)) doSignOutClear(dispatch);
          return;
        }
        if (error.response) {
          return rejectWithValue(error.response.data);
        }
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const applyBundleSelection = createAsyncThunk(
  "plan/applyBundleSelection",
  async (
    { bundleIds, billingPeriod }: { bundleIds: string[]; billingPeriod?: "month" | "year" },
    { getState, rejectWithValue },
  ) => {
    try {
      const { plan } = (getState() as { plan: ActivePlanState }).plan;
      const { data } = await axios.post(
        `${process.env.REACT_APP_SPL_URL}/subscriptions/bundles`,
        {
          bundles: bundleIds,
          billing_period: billingPeriod
            ?? plan?.billingPeriod
            ?? plan?.subscription?.billing_period
            ?? "month",
        },
        { withCredentials: true },
      );
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

// No billing_period: the switch inherits the legacy plan's own period and a differing value 400s.
// SPL removes the scheduled end-of-term cancel in this same request — never pre-call remove-cancellation.
export const acceptBundleMigration = createAsyncThunk(
  "plan/acceptBundleMigration",
  async ({ bundleIds }: { bundleIds: string[] }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_SPL_URL}/v2/subscriptions/migrate`,
        { bundles: bundleIds },
        { withCredentials: true },
      );
      return data;
    }
    catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(error.response.data);
      }
      return rejectWithValue("Unexpected error occurred");
    }
  },
);

export const activePlanSlice = createSlice({
  name: "active/plan",
  initialState,
  reducers: {
    updatePromptsLeft: (state, action: PayloadAction<number>) => {
      state.plan.promptsLeft = action.payload;
    },
    resetPlanStatus: (state) => {
      state.status = "idle";
      state.plan = initialState.plan;
      state.error = null;
    },
    setMigrationFlowOpen: (state, action: PayloadAction<boolean>) => {
      state.migrationFlowOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivePlan.pending, (state, action) => {
        if (!isSilent(action.meta.arg)) state.status = "loading";
      })
      .addCase(fetchActivePlan.fulfilled, (state, action) => {
        if (!isSilent(action.meta.arg)) state.status = "succeeded";
        applyPlanFields(state, action.payload);
      })
      .addCase(fetchActivePlan.rejected, (state, action) => {
        if (!isSilent(action.meta.arg)) {
          state.status = "failed";
          state.error = action.error.message || "Failed to fetch active plan";
        }
      })
      .addCase(applyBundleSelection.fulfilled, (state, action) => {
        const res = action.payload as Partial<ActivePlanState["plan"]> | undefined;
        if (!res || typeof res !== "object") return;
        const {
          ownedBundles, dailyPrompts, monthlySpend, billingPeriod,
          scheduledChanges, endingBundles, renewalCharge,
        } = res;
        if (ownedBundles) state.plan.ownedBundles = ownedBundles;
        if (dailyPrompts !== undefined) state.plan.dailyPrompts = dailyPrompts;
        if (monthlySpend !== undefined) state.plan.monthlySpend = monthlySpend;
        if (billingPeriod !== undefined) state.plan.billingPeriod = billingPeriod;
        if (scheduledChanges !== undefined) state.plan.scheduledChanges = scheduledChanges;
        if (endingBundles !== undefined) state.plan.endingBundles = endingBundles;
        if (renewalCharge !== undefined) state.plan.renewalCharge = renewalCharge;
      });
  },
});

export const { updatePromptsLeft, resetPlanStatus, setMigrationFlowOpen } = activePlanSlice.actions;

export default activePlanSlice.reducer;
